import React from "react";
import { TextField, Typography, Grid } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: "4rem",
    marginLeft: "2rem",
  },
  headerRow: {
    backgroundColor: "#e0e0e0",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  itemRow: {
    backgroundColor: "white",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    border: "1px solid #e0e0e0",
  },

  amountText: {
    fontWeight: "bold",
    fontSize: "1.1rem",
  },
  fieldLabel: {
    fontWeight: 600,
    fontSize: "0.9rem",
    marginBottom: theme.spacing(0.5),
  },
}));

const NewInvoiceItem = ({ onAddItem, onDeleteItem, items, setItems }) => {
  const classes = useStyles();

  // const [items, setItems] = useState([
  //   {
  //     id: Date.now(),
  //     itemName: "",
  //     unit: "",
  //     quantity: 0,
  //     price: 0.0,
  //     discPct: 0,
  //     taxPct: 0,
  //     totalAmount: 0.0,
  //     amount: 0.0,
  //     taxAmount: 0.0,
  //   },
  // ]);

  const handleItemChange = (id, field, value) => {
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          const totals = calculateTaxAmount(updatedItem);
          return {
            ...updatedItem,
            totalAmount: parseFloat(totals.baseAmount.toFixed(2)),
            taxAmount: parseFloat(totals.taxAmount.toFixed(2)),
            amount: parseFloat(
              (totals.taxableBaseAfterDiscount + totals.taxAmount).toFixed(2)
            ),
          };
        } else {
          return item;
        }
      })
    );
  };

  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      itemName: "",
      unit: "",
      quantity: 0,
      price: 0.0,
      discPct: 0,
      taxPct: 0,
    };
    const calc = calculateTaxAmount(newItem);
    const preparedItem = {
      ...newItem,
      totalAmount: parseFloat(calc.baseAmount.toFixed(2)),
      taxAmount: parseFloat(calc.taxAmount.toFixed(2)),
      amount: parseFloat(
        (calc.taxableBaseAfterDiscount + calc.taxAmount).toFixed(2)
      ),
    };
    setItems([...items, preparedItem]);
    if (onAddItem) {
      onAddItem(preparedItem);
    }
  };

  const handleDeleteItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== id));
      if (onDeleteItem) {
        onDeleteItem(id);
      }
    }
  };

  const calculateTaxAmount = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price) || 0;
    const discPct = parseFloat(item.discPct) || 0;
    const taxPct = parseFloat(item.taxPct) || 0;

    const baseAmount = quantity * price; // Taxable amount excludes tax and discount
    const discountAmount = (baseAmount * discPct) / 100;
    const taxableBaseAfterDiscount = baseAmount - discountAmount;
    const taxAmount = (taxableBaseAfterDiscount * taxPct) / 100;
    return { taxAmount, baseAmount, discountAmount, taxableBaseAfterDiscount };
  };

  const calculateTotalPriceWithTax = (item) => {
    const { taxAmount, taxableBaseAfterDiscount } = calculateTaxAmount(item);
    return (taxableBaseAfterDiscount + taxAmount).toFixed(2);
  };

  const itemsColumns = [
    {
      key: "itemName",
      label: "Line Item",
      placeholder: "placeholder",
      xs: 2.5,
    },
    { key: "unit", label: "Unit Type", xs: 1.5 },
    { key: "quantity", label: "Quantity/Hours", xs: 1.5 },
    { key: "price", label: "Unit Price ($)", xs: 1.5 },
    { key: "totalAmount", label: "Taxable Amount ($)", xs: 1, disabled: true },
    { key: "discPct", label: "Total Discount (%)", xs: 1 },
    { key: "taxPct", label: "Sales Tax (%)", xs: 1 },
    { key: "amount", label: "Amount ($)", xs: 1 },
  ];

  // useEffect(() => {
  //   // Skip emitting update while hydrating to prevent parent from re-setting props
  //   if (!initializedRef.current) return;
  //   onUpdateItem(items);
  // }, [items, onUpdateItem]);

  return (
    <div className={classes.container}>
      {/* Header Row */}
      <Grid container spacing={2} className={`${classes.headerRow}`}>
        {itemsColumns.map((col) => (
          <Grid item xs={col.xs} key={col.key}>
            <Typography className={classes.fieldLabel}>{col.label}</Typography>
          </Grid>
        ))}
        <Grid item xs={1}>
          {/* Delete button column */}
        </Grid>
      </Grid>

      {/* Item Rows */}
      {items.map((item, index) => (
        <Grid
          container
          spacing={2}
          key={item.id}
          className={classes.itemRow}
          alignItems="center"
        >
          <Grid item xs={2.5}>
            <TextField
              key={item.id}
              fullWidth
              variant="outlined"
              placeholder="Enter Item/Job Phase"
              value={item.itemName}
              onChange={(e) =>
                handleItemChange(item.id, "itemName", e.target.value)
              }
              size="small"
            />
          </Grid>
          <Grid item xs={1.5}>
            <TextField
              key={item.id}
              fullWidth
              variant="outlined"
              placeholder="Unit"
              value={item.unit || ""}
              onChange={(e) =>
                handleItemChange(item.id, "unit", e.target.value)
              }
              size="small"
            />
          </Grid>
          <Grid item xs={1.5}>
            <TextField
              key={item.id}
              fullWidth
              variant="outlined"
              type="number"
              value={item.quantity}
              onChange={(e) =>
                handleItemChange(item.id, "quantity", e.target.value)
              }
              inputProps={{ min: 0 }}
              size="small"
            />
          </Grid>
          <Grid item xs={1.5}>
            <TextField
              key={item.id}
              fullWidth
              variant="outlined"
              type="number"
              value={item.price}
              onChange={(e) =>
                handleItemChange(item.id, "price", e.target.value)
              }
              inputProps={{ min: 0, step: 0.01 }}
              size="small"
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              key={item.id}
              fullWidth
              variant="outlined"
              type="number"
              value={item.totalAmount ?? 0}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              key={item.id}
              fullWidth
              variant="outlined"
              type="number"
              value={item.discPct || ""}
              onChange={(e) =>
                handleItemChange(item.id, "discPct", e.target.value)
              }
              inputProps={{ min: 0, step: 0.01 }}
              size="small"
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              key={item.id}
              variant="outlined"
              type="number"
              value={item.taxPct || ""}
              onChange={(e) =>
                handleItemChange(item.id, "taxPct", e.target.value)
              }
              inputProps={{ min: 0, step: 0.01 }}
              size="small"
            />
          </Grid>
          <Grid item xs={1}>
            <Typography className={classes.amountText}>
              {calculateTotalPriceWithTax(item)}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <DeleteIcon
              key={item.id}
              disabled={items.length === 1}
              color="red"
              className={`${
                items.length === 1 ? "disabled" : "cursor pointer"
              }`}
              onClick={() => handleDeleteItem(item.id)}
              style={{ fontSize: "1.2rem", color: "red" }}
            />
          </Grid>
        </Grid>
      ))}

      {/* Add Item Button */}
      <button
        className="secondaryButton"
        startIcon={<AddIcon />}
        onClick={handleAddItem}
        variant="outlined"
        color="primary"
      >
        + Add Item
      </button>
    </div>
  );
};

export default NewInvoiceItem;
